import { Text, View } from "@react-pdf/renderer";
import { centsToDollars, parsePrice } from "helpers/price";
import { useMemo } from "react";
import { IUniqueProceduresByProvider } from "types/Order/Order";

import { styles } from "../styles";

export type Props = {
  proceduresByProviders: IUniqueProceduresByProvider[];
  clearPatientFeeValueInPercentage?: number | null;
};

export function ProcedureBreakdown({
  proceduresByProviders,
  clearPatientFeeValueInPercentage,
}: Props) {
  const subtotal = useMemo(() => {
    return proceduresByProviders.reduce(
      (acc: any, proceduresByProvider: any) => {
        return acc + proceduresByProvider.subtotal;
      },
      0
    );
  }, [proceduresByProviders]);
  const total = useMemo(() => {
    const clearPatientFee = clearPatientFeeValueInPercentage
      ? subtotal * (clearPatientFeeValueInPercentage / 100)
      : 0;
    return subtotal + clearPatientFee;
  }, [subtotal, clearPatientFeeValueInPercentage]);
  return (
    <View
      style={{
        marginLeft: 50,
        marginRight: 50,
        marginBottom: 20,
        marginTop: 10,
      }}
    >
      <View
        style={{
          display: "flex",
          width: "100%",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        {proceduresByProviders.map((proceduresByProvider, index) => (
          <View style={styles.table}>
            <View style={[styles.row, styles.headerRow, styles.blueBackground]}>
              <Text style={(styles.cell, styles.tableTitle)}>
                {proceduresByProvider.providerName}
              </Text>
            </View>
            {proceduresByProvider.procedures.map((procedure, index) => (
              <View style={[styles.row]} key={`${procedure.code}-${index}`}>
                <Text style={[styles.cell, styles.leftCell]}>
                  {procedure.cptDescription}
                </Text>
                <Text
                  style={[
                    styles.cell,
                    styles.rightCell,
                    styles.rightCellBorder,
                  ]}
                >
                  {parsePrice(centsToDollars(procedure.totalAmountInCents))}
                </Text>
              </View>
            ))}
            <View
              style={{
                ...styles.row,
                ...styles.greyBackground,
                fontSize: 12,
                fontFamily: "Helvetica-Bold",
                fontWeight: 900,
                marginBottom: 10,
              }}
            >
              <Text style={[styles.cell, styles.leftCell]}>Subtotal</Text>
              <Text
                style={[styles.cell, styles.rightCell, styles.rightCellBorder]}
              >
                {parsePrice(centsToDollars(proceduresByProvider.subtotal))}
              </Text>
            </View>
          </View>
        ))}
        <View
          style={{
            ...styles.row,
            ...(clearPatientFeeValueInPercentage
              ? styles.greyBackground
              : styles.blueBackground),
            fontSize: 12,
            fontFamily: "Helvetica-Bold",
            fontWeight: 900,
            marginTop: 5,
          }}
        >
          <Text style={[styles.cell, styles.leftCell]}>
            {clearPatientFeeValueInPercentage ? "Subtotal" : "Total"} Cosmetic
            Surgery Cost
          </Text>
          <Text style={[styles.cell, styles.rightCell, styles.rightCellBorder]}>
            {parsePrice(centsToDollars(subtotal))}
          </Text>
        </View>
        {clearPatientFeeValueInPercentage && (
          <>
            <View
              style={{
                ...styles.row,
                ...styles.greyBackground,
                fontSize: 12,
                fontFamily: "Helvetica-Bold",
                fontWeight: 900,
                marginTop: 5,
              }}
            >
              <Text style={[styles.cell, styles.leftCell]}>
                Payment processing and service fees
              </Text>
              <Text
                style={[styles.cell, styles.rightCell, styles.rightCellBorder]}
              >
                {parsePrice(
                  centsToDollars(
                    subtotal * (clearPatientFeeValueInPercentage / 100)
                  )
                )}
              </Text>
            </View>
            <View
              style={{
                ...styles.row,
                ...styles.blueBackground,
                fontSize: 12,
                fontFamily: "Helvetica-Bold",
                fontWeight: 900,
                marginTop: 5,
              }}
            >
              <Text style={[styles.cell, styles.leftCell]}>
                Total Cosmetic Surgery Cost
              </Text>
              <Text
                style={[styles.cell, styles.rightCell, styles.rightCellBorder]}
              >
                {parsePrice(centsToDollars(total))}
              </Text>
            </View>
          </>
        )}
      </View>
    </View>
  );
}
